import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, CircleDollarSign, ClipboardList, CreditCard, LogOut, Menu, MessageCircle, RefreshCw, Search, Settings, ShieldCheck, Users, X } from 'lucide-react';
import { authService } from '../services/authService';
import './AdminPortal.css';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

const AdminMembersPage = ({ user, onLogout }) => {
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState('');
  const [activeView, setActiveView] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadMembers = async () => {
    setLoading(true);
    setError('');
    try {
      setMembers(await authService.getRegisteredMembers());
    } catch (requestError) {
      setError(requestError.message || 'Could not load registered members.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); }, []);

  const filteredMembers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const source = activeView === 'revenue'
      ? members.filter((member) => member.paymentStatus && member.paymentStatus !== 'not_started')
      : activeView === 'plans'
        ? members.filter((member) => member.selectedPlan?.name)
        : members;
    if (!normalizedQuery) return source;
    return source.filter((member) => [member.studentName, member.parentName, member.email, member.mobile, member.city, member.role, member.selectedPlan?.name].some((value) => value?.toLowerCase().includes(normalizedQuery)));
  }, [activeView, members, query]);

  const paidMembers = members.filter((member) => member.paymentStatus === 'completed').length;
  const selectedPlans = members.filter((member) => member.selectedPlan?.name).length;
  const navigation = [
    ['analytics', 'Analytics', BarChart3],
    ['users', 'Users', Users],
    ['revenue', 'Revenue', CircleDollarSign],
    ['plans', 'Plans', ClipboardList],
    ['enquiries', 'Enquiries', MessageCircle],
    ['settings', 'Settings', Settings],
  ];
  const viewTitles = {
    analytics: 'Analytics',
    users: 'Registered Users',
    revenue: 'Revenue',
    plans: 'Plans',
    enquiries: 'Enquiries',
    settings: 'Settings',
  };
  const viewTitle = viewTitles[activeView];

  const changeView = (view) => {
    setActiveView(view);
    setSidebarOpen(false);
  };

  return (
    <section className="admin-dashboard-page" aria-label="MindVrik admin dashboard">
      <aside className={`admin-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-brand"><div><ShieldCheck size={23} /></div><span>MindVrik</span><button className="admin-close-menu" onClick={() => setSidebarOpen(false)} aria-label="Close menu"><X size={20} /></button></div>
        <p className="admin-sidebar-label">ADMIN PORTAL</p>
        <nav>
          {navigation.map(([value, label, Icon]) => <button key={value} className={activeView === value ? 'active' : ''} onClick={() => changeView(value)}><Icon size={19} />{label}</button>)}
        </nav>
        <div className="admin-sidebar-bottom"><div><span>Signed in as</span><strong>{user.email}</strong></div><button onClick={onLogout}><LogOut size={18} />Logout</button></div>
      </aside>
      {sidebarOpen && <button className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-label="Close menu" />}

      <main className="admin-dashboard-main">
        <header className="admin-dashboard-topbar"><button className="admin-menu-button" onClick={() => setSidebarOpen(true)} aria-label="Open menu"><Menu size={22} /></button><div><p>MindVrik Admin</p><h1>{viewTitle}</h1></div><button className="admin-refresh-button" onClick={loadMembers} disabled={loading}><RefreshCw size={17} className={loading ? 'is-spinning' : ''} />Refresh</button></header>

        {error && <div className="admin-form-error">{error}</div>}
        {activeView === 'analytics' && <section className="admin-stats-grid">
          <article><Users size={22} /><span>Registered Users</span><strong>{members.length}</strong><button onClick={() => changeView('users')}>View all users</button></article>
          <article><CreditCard size={22} /><span>Completed Payments</span><strong>{paidMembers}</strong><button onClick={() => changeView('revenue')}>View revenue data</button></article>
          <article><ShieldCheck size={22} /><span>Plans Selected</span><strong>{selectedPlans}</strong><button onClick={() => changeView('plans')}>View selected plans</button></article>
        </section>}

        {activeView === 'analytics' && <section className="admin-welcome-card"><div><span>MEMBER DIRECTORY</span><h2>Keep track of every MindVrik registration.</h2><p>New student and parent registrations automatically appear in Users.</p></div><button className="admin-primary-button" onClick={() => changeView('users')}><Users size={18} />Open Users</button></section>}

        {['users', 'revenue', 'plans'].includes(activeView) && <section className="admin-table-card">
          <div className="admin-table-toolbar"><div><h2>{activeView === 'users' ? 'All registered users' : activeView === 'revenue' ? 'Member payment status' : 'Members with selected plans'}</h2><p>{loading ? 'Loading latest data...' : `${filteredMembers.length} member${filteredMembers.length === 1 ? '' : 's'} shown`}</p></div><label className="admin-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, email, mobile..." /></label></div>
          <div className="admin-table-wrap"><table><thead><tr><th>Member</th><th>Contact</th><th>Role</th><th>Location</th><th>Plan</th><th>Payment</th><th>Registered</th></tr></thead><tbody>{filteredMembers.map((member) => <tr key={member.id}><td><strong>{member.studentName || member.parentName || 'Member'}</strong><small>{member.parentName && member.parentName !== member.studentName ? member.parentName : ''}</small></td><td>{member.email}<small>{member.mobile || '-'}</small></td><td className="admin-capitalize">{member.role || '-'}</td><td>{member.city || '-'}<small>{member.grade || ''}</small></td><td>{member.selectedPlan?.name || 'Not selected'}</td><td><span className={`admin-payment-status ${member.paymentStatus === 'completed' ? 'completed' : ''}`}>{member.paymentStatus || 'not started'}</span></td><td>{formatDate(member.createdAt)}</td></tr>)}</tbody></table>{!loading && !filteredMembers.length && <p className="admin-empty-state">No registered members found.</p>}</div>
        </section>}

        {activeView === 'enquiries' && <section className="admin-table-card admin-info-panel"><MessageCircle size={27} /><h2>Enquiries</h2><p>There are no customer enquiries yet. When the contact form is connected to the backend, new enquiries will be displayed here.</p></section>}
        {activeView === 'settings' && <section className="admin-table-card admin-info-panel"><Settings size={27} /><h2>Admin Settings</h2><p>You are signed in as <strong>{user.email}</strong>. This portal is protected and only admin accounts can access member data.</p><button className="admin-refresh-button" onClick={onLogout}><LogOut size={17} />Logout securely</button></section>}
      </main>
    </section>
  );
};

export default AdminMembersPage;
